import type { Validation } from "@/api/generated/model";
import { useSubchapterValidations } from "@/hooks/useSubchapterValidations";
import {
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { QuizReviewItem } from "@/lib/quizReview";

interface QuizValidations {
  status: InteractiveElementStatus | undefined;
  /** False while the first validation_data fetch is in flight. */
  isReady: boolean;
  /**
   * Refetches the saved answers. Call it after a submit: the status derived
   * from an earlier fetch would otherwise still say "in progress" after the
   * learner finished, and it wins over the finished state the quiz just set.
   */
  refresh: () => void;
}

type BuildReviewItem = (validation: Validation) => QuizReviewItem;

const resolveQuizValidationStatus = (
  data: Validation[] | undefined,
  contentElementId: string,
  entryCount: number,
  buildPartial?: (answeredCount: number, correctCount: number) => unknown,
  buildReviewItem?: BuildReviewItem
): InteractiveElementStatus | undefined => {
  if (!data) return undefined;

  const completed = data
    .filter(
      (i) => i.contentElementId === contentElementId && i.status === "completed"
    )
    .sort((a, b) => a.entryIndex - b.entryIndex);

  if (completed.length === 0) return undefined;

  const correctCount = completed.filter((c) => c.result.correct).length;

  if (completed.length < entryCount) {
    if (!buildPartial) return undefined;
    // Resume from the server: rebuild the quiz's partial from the answered
    // entries and reopen at the first unanswered question.
    return {
      kind: InteractiveElementStatusKind.InProgress,
      partial: buildPartial(completed.length, correctCount),
      lastTaskIndex: completed.length,
    };
  }

  const items: QuizReviewItem[] = completed.map(
    (c) =>
      buildReviewItem?.(c) ?? {
        kind: "options",
        prompt: c.result.prompt ?? "",
        options: c.result.options ?? [],
        isCorrect: !!c.result.correct,
        feedback: c.result.feedback || undefined,
      }
  );

  return {
    kind: InteractiveElementStatusKind.Completed,
    score: { correct: correctCount, total: entryCount },
    attempts: 1,
    completedAt: "",
    review: { items },
  };
};

/**
 * Loads this element's validation_data for the current subchapter. Used to
 * override the card with a completed review, or (when `buildPartial` is given)
 * to resume in-progress from the server. `isReady` is true once that fetch has
 * settled — or immediately when no fetch will run.
 */
export const useQuizValidations = (
  contentElementId: string,
  entryCount: number,
  /**
   * When provided, lets a quiz resume in-progress from the server: if some — but
   * not all — entries have been validated, this builds the quiz's partial state
   * from the answered/correct counts and `status` is an InProgress override.
   * Omit it to keep the "all-or-nothing" behaviour: only a fully answered quiz
   * yields a status.
   */
  buildPartial?: (answeredCount: number, correctCount: number) => unknown,
  /**
   * Builds the review item of one validated entry. Omit it for quizzes whose
   * server result already carries an option breakdown (multiple choice,
   * true/false); quizzes with another shape (cloze gaps, matching pairs, code
   * snippet) build it from their entry and the stored `userInput`.
   */
  buildReviewItem?: BuildReviewItem
): QuizValidations => {
  const { data, isLoading, mutate } = useSubchapterValidations(
    contentElementId,
    entryCount > 0
  );

  return {
    status: resolveQuizValidationStatus(
      data,
      contentElementId,
      entryCount,
      buildPartial,
      buildReviewItem
    ),
    isReady: !isLoading,
    refresh: () => void mutate(),
  };
};
